import { LoggerImplement } from "@iflyrpa/share";
import { SocksProxyAgent } from "socks-proxy-agent";
import { Http } from "./http";

interface ProxyInfo {
    code: string;
    desc: string;
    data: {
        proxyIp: string;
        proxyPort: string;
    } | null;
}

// 代理鉴权凭证表：每个 IP + 端口区间对应一组账号/密码（来自 a.md）。
// portStart/portEnd 为闭区间，例如 44001-44050 表示端口 44001 到 44050。
interface ProxyCredential {
    ip: string;
    portStart: number;
    portEnd: number;
    user: string;
    pass: string;
}

const PROXY_CREDENTIALS: ProxyCredential[] = [
    {
        ip: "47.110.131.129",
        portStart: 44001,
        portEnd: 44050,
        user: "m64ut33hvzUzuh",
        pass: "pV2nKbhvgciM3URoRjnvk5D1d0CryaE",
    },
    {
        ip: "47.110.131.129",
        portStart: 44051,
        portEnd: 44100,
        user: "m64uz9EMrVpaCk",
        pass: "pAhDnBmdn12odgffGJnbFt9Jts6EiRz",
    },
    {
        ip: "47.110.131.129",
        portStart: 44101,
        portEnd: 44150,
        user: "m64uacZLZ1hQuz",
        pass: "p0oyBQtTFXk9x5BniP1GT3t5zmy1kWX",
    },
    {
        ip: "47.110.131.129",
        portStart: 44151,
        portEnd: 44200,
        user: "m64ugn30hbrv5w",
        pass: "pDMlGki6kx5ObJmIVNX9IyaTyLCNdwF",
    },
    {
        ip: "47.110.131.129",
        portStart: 44201,
        portEnd: 44250,
        user: "m64uxrjtLEuuiJ",
        pass: "pgSS3tw48oFBFKieAjJjsCzfdbvOxyD",
    },
    {
        ip: "47.110.131.129",
        portStart: 45001,
        portEnd: 45050,
        user: "m63uGNqJq3SfCR",
        pass: "p9gDMdIlxrfwK6DPpYxad6nnLy2oz7b",
    },
    {
        ip: "47.110.131.129",
        portStart: 45051,
        portEnd: 45100,
        user: "m63usvfyWYu6vY",
        pass: "pu0hLZL81K5u43XbWlWKjZsNOAPG238",
    },
    {
        ip: "47.110.131.129",
        portStart: 45101,
        portEnd: 45150,
        user: "m63uI2pvHbB0b0",
        pass: "p353azuJBlrzuu2EidptuhdnBIybdUy",
    },
    {
        ip: "47.110.131.129",
        portStart: 45151,
        portEnd: 45200,
        user: "m63uSpJbw46qnQ",
        pass: "pYWdnwHHXoKgIOLfuvruZFlSaGN2ZVO",
    },
    {
        ip: "47.110.131.129",
        portStart: 45201,
        portEnd: 45250,
        user: "m63ufKz8YekqZq",
        pass: "phDmq6dBU85Z85A99LsrtluBgG5OFQ2",
    },
];

/**
 * 根据 IP 与端口查找对应的代理账号/密码。
 * 端口落在某条记录的 [portStart, portEnd] 闭区间内即命中，未命中返回 null。
 */
function findProxyCredential(ip: string, port: string): ProxyCredential | null {
    const portNum = Number(port);
    if (Number.isNaN(portNum)) return null;
    return PROXY_CREDENTIALS.find((c) => c.ip === ip && portNum >= c.portStart && portNum <= c.portEnd) ?? null;
}

/**
 * 构建带鉴权信息的 socks5h 代理 URL。
 * 使用 socks5h（h 表示由代理服务器做 DNS 解析），凭证写入 userinfo 部分，
 * SocksProxyAgent 会据此向代理服务器做 SOCKS5 用户名/密码鉴权。
 * 根据 ip + port 从凭证表查找对应账号密码，未命中则返回不带鉴权的 URL。
 */
function buildProxyUrl(ip: string, port: string): string {
    const credential = findProxyCredential(ip, port);
    if (credential) {
        // 对凭证做 URL 编码，避免用户名/密码中含有 `:`、`@`、`/` 等特殊字符破坏 URL 结构
        const user = encodeURIComponent(credential.user);
        const pass = encodeURIComponent(credential.pass);
        return `socks5h://${user}:${pass}@${ip}:${port}`;
    }
    return `socks5h://${ip}:${port}`;
}

export async function ProxyAgent(
    task: { logger?: LoggerImplement },
    ip: string,
    adr?: string | null,
    accountId?: number,
    refresh?: boolean,
) {
    const http = new Http({
        headers: {
            "content-type": "application/json",
        },
    });
    const params = {
        ip: ip,
        addr: adr || null,
        accountId: accountId,
    };
    const ProxyInfo: ProxyInfo = await http
        .api({
            method: "POST",
            url: "https://fetdev.iflysec.com/ip-pool/pool/eip/proxy",
            data: params,
        })
        .catch((err) => {
            throw { code: 414, message: `请求代理失败：${err.message}`, data: {} };
        });

    const loggerInfo = {
        params,
        response: {
            code: ProxyInfo.code,
            desc: ProxyInfo.desc,
            data: ProxyInfo.data,
        },
    };

    task.logger?.info(
        `请求代理：区域：${params.addr}, IP：${params.ip}, AccountId：${params.accountId}, 返回代理信息: ${ProxyInfo.data ? `${ProxyInfo.data.proxyIp}:${ProxyInfo.data.proxyPort}` : "无可用代理"}`,
        loggerInfo,
    );

    // 在 URL 中写上认证信息，使用 SocksProxyAgent 支持 socks5h 协议
    const proxyAgent =
        ProxyInfo.code === "000000" && ProxyInfo.data
            ? {
                  ip: ProxyInfo.data.proxyIp,
                  port: ProxyInfo.data.proxyPort,
                  agent: new SocksProxyAgent(buildProxyUrl(ProxyInfo.data.proxyIp, ProxyInfo.data.proxyPort)),
              }
            : null;

    return proxyAgent;
}
